import { useState, forwardRef, useRef, useEffect } from 'react';
import { COLORS, TYPOGRAPHY, INPUT_TOKENS } from '../../../styles/UISystem';

/**
 * FormConnectionNote Component
 *
 * Auto-expanding textarea component for connection notes
 * - Dynamically expands as content is added
 * - Max height: 150px (then scrolls)
 * - Max characters: 500
 * - placeholder: Shows helper text in light gray
 * - active: Shows cursor, placeholder text disappears
 * - filled: Shows entered text
 */
const MAX_CHARS = 500;

// Variant dimensions
const VARIANTS = {
  default: {
    minHeight: 36,
    maxHeight: 150,
    padding: '10px 16px',
    borderRadius: INPUT_TOKENS.NOTE_RADIUS,
    backgroundColor: INPUT_TOKENS.PILL_BG,
    boxShadow: INPUT_TOKENS.NOTE_SHADOW,
  },
};

export const FormConnectionNote = forwardRef(
  ({ value = '', placeholder = 'Add note', onChange, onBlur, onFocus, ...props }, ref) => {
    const dims = VARIANTS.default;
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef(null);

    // Use forwarded ref or internal ref
    const actualRef = ref || textareaRef;

    // Auto-resize textarea based on content
    useEffect(() => {
      const textarea = actualRef.current;
      if (textarea) {
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        // Set height to scrollHeight, but respect min/max
        const newHeight = Math.min(Math.max(textarea.scrollHeight, dims.minHeight), dims.maxHeight);
        textarea.style.height = `${newHeight}px`;
        // Enable scrolling if content exceeds max height
        textarea.style.overflowY = textarea.scrollHeight > dims.maxHeight ? 'auto' : 'hidden';
      }
    }, [value, actualRef, dims.minHeight, dims.maxHeight]);

    // Handle input changes with character limit
    const handleChange = (e) => {
      const newValue = e.target.value;
      // Enforce character limit
      if (newValue.length <= MAX_CHARS) {
        if (onChange) {
          onChange(e);
        }
      }
    };

    // Handle focus event
    const handleFocus = (e) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    };

    // Handle blur event
    const handleBlur = (e) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    };

    // Determine visual state based on focus and content
    const hasContent = value && value.length > 0;
    const state = isFocused ? 'active' : hasContent ? 'filled' : 'placeholder';

    // Text styling based on state
    const getTextStyle = () => {
      if (state === 'placeholder') {
        return {
          ...TYPOGRAPHY.BODY_LIGHT,
          fontWeight: '300',
          color: COLORS.MEDIUM_GRAY,
        };
      }
      return {
        ...TYPOGRAPHY.BODY_REGULAR,
        color: COLORS.DARK_GRAY,
      };
    };

    return (
      <textarea
        ref={actualRef}
        placeholder={!isFocused ? placeholder : ''}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        maxLength={MAX_CHARS}
        style={{
          width: '100%',
          maxWidth: '340px',
          minHeight: `${dims.minHeight}px`,
          maxHeight: `${dims.maxHeight}px`,
          backgroundColor: dims.backgroundColor,
          border: 'none',
          outline: 'none',
          borderRadius: dims.borderRadius,
          padding: dims.padding,
          boxSizing: 'border-box',
          resize: 'none',
          overflowY: 'hidden',
          boxShadow: dims.boxShadow,
          ...getTextStyle(),
          ...(props.style || {}),
        }}
        {...props}
      />
    );
  }
);

FormConnectionNote.displayName = 'FormConnectionNote';

/**
 * FormConnectionNoteExample Component
 *
 * Used for displaying FormConnectionNote examples in the UI System
 */
export const FormConnectionNoteExample = () => {
  const [placeholderValue, setPlaceholderValue] = useState('');
  const [filledValue, setFilledValue] = useState(
    'Met at Royale conference in San Francisco. Works at Google.'
  );

  return (
    <div style={{ marginBottom: '40px' }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLORS.DARK_GRAY,
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        FormConnectionNote
      </div>

      {/* Placeholder state */}
      <div style={{ marginBottom: '32px' }}>
        <div
          style={{
            marginBottom: '8px',
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            textAlign: 'center',
          }}
        >
          placeholder
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FormConnectionNote
            state="placeholder"
            placeholder="Add note"
            value={placeholderValue}
            onChange={(e) => setPlaceholderValue(e.target.value)}
          />
        </div>
      </div>

      {/* Filled state */}
      <div style={{ marginBottom: '32px' }}>
        <div
          style={{
            marginBottom: '8px',
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            textAlign: 'center',
          }}
        >
          filled
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FormConnectionNote
            state="filled"
            value={filledValue}
            onChange={(e) => setFilledValue(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default FormConnectionNote;
